// LA SÉRIE DE PIÈCES (26/07, chantier « plus ludique et jouissif »).
//
// Constat mesuré en production avant d'écrire une ligne : sur les sept premiers niveaux du parcours,
// la moyenne de morts par joueur va de 0,0 à 0,9. On traverse le jeu sans mourir — donc sans jamais
// triompher non plus. Ramasser une pièce ne rapporte rien de plus que la pièce.
//
// La série récompense ce qu'un chrono ne voit pas : l'ENCHAÎNEMENT. Prendre trois pièces coup sur
// coup demande de choisir son trajet à l'avance ; le compteur rend cette intention visible, et le
// son qui monte la rend agréable. C'est la boucle « encore une » : rien à débloquer, tout à refaire
// mieux.
//
// Module PUR (ni DOM, ni canvas, ni simulation) : rien ici n'entre dans le hash d'état, et la règle
// se vérifie en Node.

/**
 * Au-delà de ce délai sans ramasser, la série retombe. 3 s à 30 Hz.
 *
 * ⚠️ Chiffre CALIBRÉ sur les vraies cartes, pas choisi au jugé : le niveau 01 compte 4 pièces pour
 * un record de 10,7 s, soit 2,7 s entre deux pièces en moyenne. Une fenêtre de 1,5 s (le premier
 * réglage) rendait la série tout bonnement inatteignable là où la famille joue le plus — une
 * récompense qu'on ne voit jamais n'existe pas. 3 s laisse la chaîne vivante quand on ne traîne
 * pas, et la casse dès qu'on hésite.
 */
export const SERIE_TICKS = 90

/** À partir de ce compte, la série s'affiche et sonne. En dessous, deux pièces voisines ne sont pas
 *  un exploit et un compteur qui clignote sans arrêt cesse d'être une récompense. */
export const SERIE_MIN = 3

export interface Serie {
  /** Nombre de pièces enchaînées (0 = aucune série en cours). */
  n: number
  /** Tick de la dernière pièce ramassée. */
  dernier: number
  /** La plus longue série du niveau, pour l'écran de victoire. */
  record: number
}

export const SERIE_VIDE: Serie = { n: 0, dernier: 0, record: 0 }

/**
 * Fait avancer la série d'un tick.
 *
 * `pieces` = combien de pièces le coureur local a ramassées CE tick (0 le plus souvent). Deux pièces
 * dans le même tick comptent deux : c'est un enchaînement encore plus serré, pas un doublon.
 *
 * La série retombe à 0 dès que `SERIE_TICKS` s'écoulent sans rien ramasser — mais le RECORD du
 * niveau, lui, ne retombe jamais : c'est ce qu'on montre à la fin.
 */
export function serieSuivante(s: Serie, tick: number, pieces: number): Serie {
  if (pieces > 0) {
    // La série continue si la dernière pièce est assez proche ; sinon on repart à cette pièce-ci.
    const enCours = s.n > 0 && tick - s.dernier <= SERIE_TICKS
    const n = (enCours ? s.n : 0) + pieces
    return { n, dernier: tick, record: Math.max(s.record, n) }
  }
  if (s.n > 0 && tick - s.dernier > SERIE_TICKS) return { n: 0, dernier: s.dernier, record: s.record }
  return s
}

/** Vrai si la série mérite d'être montrée (et sonnée) à ce tick. */
export function serieVisible(s: Serie): boolean {
  return s.n >= SERIE_MIN
}

/**
 * La hauteur du son d'une pièce, en hertz. Elle MONTE avec la série : c'est la récompense la plus
 * immédiate, celle qu'on entend avant même de lire le compteur. Plafonnée pour rester musicale
 * (au-delà, ça devient un sifflement) et pour qu'une longue série ne devienne pas désagréable.
 */
export function serieHz(n: number): number {
  const marches = Math.min(Math.max(n - 1, 0), 12)
  return Math.round(880 * Math.pow(2, marches / 12)) // un demi-ton par pièce, une octave au plus
}
